import React, { useCallback, useState } from 'react';
import { AppStateUser } from '../../../client/providers/AppProvider';
import { CloseIcon } from '../../svg/CloseIcon';
import { SendIcon } from '../../svg/dashboard/messages/SendIcon';
import { ConversationNavigation } from './ConversationNavigation';
import {
    Espin_Role_Enum,
    CreateMessageMutation,
    CreateMessageMutationVariables,
    CreateMessageDocument,
    Espin_Message_Type_Enum,
    Content_File,
} from '../../../generated/spin-graphql';
import { useAuthenticatedMutation } from '../../../client/hooks';
import { PictureWithFallback } from '../../sections/PictureWithFallback';
import Link from 'next/link';
import { useRouter } from 'next/router';

type Props = {
    user: AppStateUser;
    initialMessageContent: InitialMessageContent;
    onBackClicked: () => void;
};

export type InitialMessageContent = {
    player_user_id?: string;
    college_id: string;
    college_user_id?: string;
    high_school_user_id?: string;
    high_school_id?: string;
    parent_user_id?: string;
    conversantName: string;
    slug: string;
    profileImage: Partial<Content_File>;
};

export function getMessageType(user: AppStateUser, isHighSchoolInConversation: boolean) {
    if (user.role === Espin_Role_Enum.EspinCollegeUser) {
        return isHighSchoolInConversation
            ? Espin_Message_Type_Enum.CollegeMessagesHighSchool
            : Espin_Message_Type_Enum.CollegeMessagesPlayer;
    } else if (user.role === Espin_Role_Enum.EspinHighschoolUser) {
        return Espin_Message_Type_Enum.HighSchoolMessagesCollege;
    } else {
        return Espin_Message_Type_Enum.PlayerMessagesCollege;
    }
}

export const InitialConversationContent: React.FC<Props> = ({ user, initialMessageContent, onBackClicked }) => {
    const [selectedMessage, setSelectedMessage] = useState<string>(undefined);
    const router = useRouter();
    const {
        player_user_id,
        college_id,
        college_user_id,
        parent_user_id,
        high_school_user_id,
        high_school_id,
        conversantName,
        slug,
        profileImage,
    } = initialMessageContent;
    const [result, createMessageMutation] = useAuthenticatedMutation<
        CreateMessageMutation,
        CreateMessageMutationVariables
    >(CreateMessageDocument);

    const sendMessage = useCallback(async () => {
        const result = await createMessageMutation({
            input: {
                player_user_id,
                college_id,
                college_user_id,
                parent_user_id,
                high_school_user_id,
                high_school_id,
                message_type: getMessageType(user, !!high_school_user_id || !!high_school_id),
                contents: selectedMessage,
            },
        });

        if (result.error) {
            console.error(result.error);
            return;
        }
    }, [
        createMessageMutation,
        player_user_id,
        college_id,
        college_user_id,
        parent_user_id,
        high_school_user_id,
        high_school_id,
        user,
        selectedMessage,
    ]);

    return (
        <>
            <div className="header-container">
                <div className="image-wrapper">
                    <PictureWithFallback
                        width={40}
                        height={40}
                        icon={profileImage}
                        alt={`${conversantName}'s profile picture`}
                    />
                </div>
                <Link href={`/${user.role === Espin_Role_Enum.EspinCollegeUser ? 'players' : 'colleges'}/${slug}`}>
                    <a className="username">{conversantName}</a>
                </Link>
                <button onClick={onBackClicked} className="button circle-button back-button">
                    <CloseIcon />
                </button>
            </div>
            <div className="content">
                <div className="no-conversation-selected-container">
                    <h2 className="title">Start the Conversation</h2>
                    <p className="subtitle">Choose a Greeting from your conversation list.</p>
                </div>
            </div>
            <div className="conversation-controls">
                {!!selectedMessage ? (
                    <>
                        <div className="selected-message">
                            {selectedMessage}
                            <button className="button close-button" onClick={() => setSelectedMessage(undefined)}>
                                <CloseIcon />
                            </button>
                        </div>
                        <button className="button circle-button send-button" onClick={() => sendMessage()}>
                            <SendIcon />
                        </button>
                    </>
                ) : (
                    <ConversationNavigation
                        isLocked={false}
                        user={user}
                        conversantName={conversantName}
                        setSelectedMessage={(message) => setSelectedMessage(message)}
                    />
                )}
            </div>
        </>
    );
};
